'use client';

import { type RefObject, useEffect, useRef, useState } from 'react';

/** Seconds a single arrow press moves the playhead. */
export const STEP_SECONDS = 10;

/** The most recent scrub, for on-screen feedback. `seq` increments on every
 *  press so a repeated press in the same direction restarts the animation
 *  rather than looking like nothing happened. */
export interface SeekFeedback {
  direction: 1 | -1;
  seq: number;
}

/** How long the on-screen readout stays up. */
const FEEDBACK_MS = 650;

/** Typing somewhere should never scrub a video playing elsewhere on the page. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * Left and right arrows scrub a playing video by ten seconds.
 *
 * Bound to the window rather than the video, because the video is not focusable
 * here — it has no controls, it is played through an overlay button — so a
 * listener on the element itself would only fire if the visitor happened to tab
 * to it first.
 *
 * Only while `active` is true, which is the playing state. A page with a paused
 * hero still needs its arrow keys for scrolling and for the carousels.
 *
 * Not mirrored for RTL: media transport is physical everywhere that ships it
 * (right is later, left is earlier) and an Arabic visitor pressing right to
 * skip forward would be surprised by the opposite.
 */
export function useVideoSeekKeys(
  ref: RefObject<HTMLVideoElement | null>,
  active: boolean,
): SeekFeedback | null {
  const [feedback, setFeedback] = useState<SeekFeedback | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!active) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextEntry(event.target)) return;

      const video = ref.current;
      if (!video) return;

      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const delta = event.key === 'ArrowRight' ? STEP_SECONDS : -STEP_SECONDS;
      // Clamped just short of the end: seeking exactly to duration fires
      // `ended` on some browsers, so a skip near the end would stop playback
      // rather than move the playhead.
      video.currentTime = Math.min(
        Math.max(video.currentTime + delta, 0),
        Math.max(duration - 0.1, 0),
      );

      seqRef.current += 1;
      setFeedback({ direction: delta > 0 ? 1 : -1, seq: seqRef.current });

      // The arrows would otherwise scroll the page under the video.
      event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ref, active]);

  // Clear the readout a beat after the last press. Keyed on seq, so holding an
  // arrow down keeps it up instead of flickering between presses.
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), FEEDBACK_MS);
    return () => clearTimeout(t);
  }, [feedback]);

  // Nothing to show once playback stops. Read at render rather than cleared in
  // an effect — a synchronous setState there cascades an extra render, and the
  // stale value would paint for a frame first.
  return active ? feedback : null;
}

export default useVideoSeekKeys;
